import {
  Check,
  Download,
  LayoutGrid,
  Layers,
  List as ListIcon,
  Plus,
  Search,
  Table as TableIcon,
  Trash2,
  X,
} from 'lucide-react';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { SortableTableHead } from '@/components/ui/sortable-table-head';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { ViewModeToggle, type ViewModeOption } from '@/components/ui/view-mode-toggle';
import { exportCsv, type ApiError } from '@/core/api';
import { useEmployees } from '@/features/hrm/employees/queries';
import { employeeName } from '@/features/hrm/employees/types';
import { useMembers } from '@/features/members';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { useSession } from '@/stores/session';
import { BASE } from '../api';
import {
  LEAVE_STATUSES,
  LEAVE_STATUS_LABEL,
  LEAVE_TYPES,
  LEAVE_TYPE_LABEL,
  type LeaveStatus,
} from '../constants';
import {
  useApproveTimeOff,
  useDeleteTimeOff,
  useRejectTimeOff,
  useTimeOff,
} from '../queries';
import { useTimeOffUi, type TimeOffViewMode } from '../store/viewPrefs';
import type { TimeOff, TimeOffInput } from '../types';

const ALL = 'all';

/** Columns the backend lets us order by — keeps typos out of the URL. */
type TimeOffOrderingColumn = 'start_date' | 'end_date' | 'status' | 'type';

const STATUS_VARIANT: Record<LeaveStatus, 'secondary' | 'outline' | 'destructive'> = {
  requested: 'outline',
  approved: 'secondary',
  rejected: 'destructive',
  cancelled: 'outline',
};

const HR_ROLES = new Set(['founder', 'super_admin', 'hr']);

const VIEW_MODES: readonly ViewModeOption<TimeOffViewMode>[] = [
  { value: 'table', label: 'Table', icon: TableIcon },
  { value: 'list', label: 'List', icon: ListIcon },
  { value: 'cards', label: 'Cards', icon: LayoutGrid },
  { value: 'grouped', label: 'Grouped by status', icon: Layers },
];

function formatDate(value: string | null): string {
  return value ? new Date(value).toLocaleDateString() : '—';
}

/** Resolved labels a row view needs, kept out of each renderer. */
interface RowLabels {
  employeeLabel: (id: number) => string;
  approverLabel: (id: number | null) => string;
}

export function TimeOffListPage() {
  const navigate = useNavigate();
  const role = useSession((s) => s.role);
  const canApprove = role ? HR_ROLES.has(role) : false;

  const view = useTimeOffUi((s) => s.view);
  const setView = useTimeOffUi((s) => s.setView);
  const ordering = useTimeOffUi((s) => s.ordering);
  const setOrdering = useTimeOffUi((s) => s.setOrdering);

  // Server-driven filters live in local state — pagination + search hit the API.
  const [searchInput, setSearchInput] = useState('');
  // Live, debounced search — fires 300ms after typing stops (no Search button).
  const search = useDebouncedValue(searchInput.trim(), 300);
  const [statusFilter, setStatusFilter] = useState<LeaveStatus | typeof ALL>(ALL);
  const [typeFilter, setTypeFilter] = useState<string>(ALL);
  const [page, setPage] = useState(1);

  const [toDelete, setToDelete] = useState<TimeOff | null>(null);

  const { data, isPending, isError, error, isFetching } = useTimeOff({
    page,
    search: search || undefined,
    status: statusFilter === ALL ? undefined : statusFilter,
    type: typeFilter === ALL ? undefined : (typeFilter as TimeOffInput['type']),
    ordering,
  });
  const { data: employeesData } = useEmployees({});
  const { data: membersData } = useMembers();

  const employeeNames = useMemo(() => {
    const map = new Map<number, string>();
    for (const e of employeesData?.results ?? []) map.set(e.id, employeeName(e));
    return map;
  }, [employeesData]);
  const approverNames = useMemo(() => {
    const map = new Map<number, string>();
    for (const m of membersData?.results ?? []) map.set(m.user.id, m.user.full_name);
    return map;
  }, [membersData]);

  const labels: RowLabels = useMemo(
    () => ({
      employeeLabel: (id) => employeeNames.get(id) ?? `#${id}`,
      approverLabel: (id) => (id ? (approverNames.get(id) ?? '…') : '—'),
    }),
    [employeeNames, approverNames],
  );

  const approve = useApproveTimeOff();
  const reject = useRejectTimeOff();
  const remove = useDeleteTimeOff();

  const rows = data?.results ?? [];

  const open = (r: TimeOff) => navigate(`/hrm/time-off/${r.public_id}`);

  const actions: RowActions = {
    canApprove,
    onOpen: open,
    onApprove: (r) => approve.mutate(r.id),
    approveDisabled: approve.isPending,
    onReject: (r) => reject.mutate(r.id),
    rejectDisabled: reject.isPending,
    onDelete: (r) => setToDelete(r),
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-2xl font-semibold tracking-tight">Time Off</h1>
        <Button onClick={() => navigate('/hrm/time-off/new')}>
          <Plus className="size-4" /> Request time off
        </Button>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative max-w-sm flex-1">
            <Search className="text-muted-foreground absolute top-1/2 left-2.5 size-4 -translate-y-1/2" />
            <Input
              value={searchInput}
              onChange={(e) => {
                setPage(1);
                setSearchInput(e.target.value);
              }}
              placeholder="Search by employee or reason…"
              className="pl-8"
              aria-label="Search time-off requests"
            />
          </div>
          <Select
            value={statusFilter}
            onValueChange={(v) => {
              setPage(1);
              setStatusFilter(v as LeaveStatus | typeof ALL);
            }}
          >
            <SelectTrigger className="w-44">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>All statuses</SelectItem>
              {LEAVE_STATUSES.map((s) => (
                <SelectItem key={s.value} value={s.value}>
                  {s.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select
            value={typeFilter}
            onValueChange={(v) => {
              setPage(1);
              setTypeFilter(v);
            }}
          >
            <SelectTrigger className="w-44">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>All types</SelectItem>
              {LEAVE_TYPES.map((t) => (
                <SelectItem key={t.value} value={t.value}>
                  {t.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-center gap-2">
          <Button
            type="button"
            size="icon"
            variant="outline"
            onClick={() => {
              // Mirror the live list filters and add `export=csv`; the backend
              // returns `Content-Disposition: attachment`, so this navigation
              // downloads `time-off.csv` without leaving the SPA.
              void exportCsv(BASE, {
                search: search || undefined,
                status: statusFilter === ALL ? undefined : statusFilter,
                type: typeFilter === ALL ? undefined : typeFilter,
                ordering,
                export: 'csv',
              });
            }}
            aria-label="Export time-off requests as CSV"
            title="Export time-off requests as CSV"
          >
            <Download className="size-4" />
          </Button>
          <ViewModeToggle value={view} onChange={setView} modes={VIEW_MODES} />
        </div>
      </div>

      {isError ? (
        <p className="text-destructive text-sm">
          {(error as unknown as ApiError)?.message ?? 'Failed to load requests.'}
        </p>
      ) : isPending ? (
        <TimeOffSkeleton />
      ) : rows.length === 0 ? (
        <TimeOffTable
          rows={[]}
          labels={labels}
          actions={actions}
          ordering={ordering}
          onOrderingChange={setOrdering}
        />
      ) : view === 'table' ? (
        <TimeOffTable
          rows={rows}
          labels={labels}
          actions={actions}
          ordering={ordering}
          onOrderingChange={setOrdering}
        />
      ) : view === 'list' ? (
        <TimeOffList rows={rows} labels={labels} actions={actions} />
      ) : view === 'cards' ? (
        <TimeOffCards rows={rows} labels={labels} actions={actions} />
      ) : (
        <TimeOffGroupedByStatus rows={rows} labels={labels} actions={actions} />
      )}

      {data && data.count > 0 ? (
        <div className="flex items-center justify-between">
          <p className="text-muted-foreground text-sm">
            {data.count} {data.count === 1 ? 'request' : 'requests'}
          </p>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={!data.previous || isFetching}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              Previous
            </Button>
            <span className="text-sm">Page {page}</span>
            <Button
              variant="outline"
              size="sm"
              disabled={!data.next || isFetching}
              onClick={() => setPage((p) => p + 1)}
            >
              Next
            </Button>
          </div>
        </div>
      ) : null}

      <AlertDialog open={Boolean(toDelete)} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete request?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently remove the record.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (toDelete) remove.mutate(toDelete.id);
                setToDelete(null);
              }}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// ---- Shared row helpers ----------------------------------------------------

interface RowActions {
  canApprove: boolean;
  onOpen: (r: TimeOff) => void;
  onApprove: (r: TimeOff) => void;
  approveDisabled: boolean;
  onReject: (r: TimeOff) => void;
  rejectDisabled: boolean;
  onDelete: (r: TimeOff) => void;
}

interface ViewProps {
  rows: TimeOff[];
  labels: RowLabels;
  actions: RowActions;
}

/** The approve/reject/delete cluster, shared across every view. */
function RowActionButtons({ row, actions }: { row: TimeOff; actions: RowActions }) {
  const isPendingDecision = row.status === 'requested';
  return (
    // Stop clicks bubbling to the row's open handler so action buttons act alone.
    <span
      className="inline-flex items-center"
      onClick={(e) => e.stopPropagation()}
      role="presentation"
    >
      {actions.canApprove && isPendingDecision ? (
        <>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => actions.onApprove(row)}
            disabled={actions.approveDisabled}
          >
            <Check className="size-4" /> Approve
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => actions.onReject(row)}
            disabled={actions.rejectDisabled}
          >
            <X className="size-4" /> Reject
          </Button>
        </>
      ) : null}
      <Button
        size="icon"
        variant="ghost"
        aria-label="Delete request"
        onClick={() => actions.onDelete(row)}
      >
        <Trash2 className="size-4" />
      </Button>
    </span>
  );
}

function StatusBadge({ status }: { status: LeaveStatus }) {
  return <Badge variant={STATUS_VARIANT[status]}>{LEAVE_STATUS_LABEL[status]}</Badge>;
}

// ---- View renderers --------------------------------------------------------

interface TimeOffTableProps extends ViewProps {
  ordering: string | undefined;
  onOrderingChange: (next: string | undefined) => void;
}

function TimeOffTable({
  rows,
  labels,
  actions,
  ordering,
  onOrderingChange,
}: TimeOffTableProps) {
  return (
    <div className="rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Employee</TableHead>
            <SortableTableHead<TimeOffOrderingColumn>
              column="type"
              value={ordering}
              onChange={onOrderingChange}
            >
              Type
            </SortableTableHead>
            <SortableTableHead<TimeOffOrderingColumn>
              column="status"
              value={ordering}
              onChange={onOrderingChange}
            >
              Status
            </SortableTableHead>
            <SortableTableHead<TimeOffOrderingColumn>
              column="start_date"
              value={ordering}
              onChange={onOrderingChange}
            >
              Start
            </SortableTableHead>
            <SortableTableHead<TimeOffOrderingColumn>
              column="end_date"
              value={ordering}
              onChange={onOrderingChange}
              className="hidden sm:table-cell"
            >
              End
            </SortableTableHead>
            <TableHead className="hidden md:table-cell">Approver</TableHead>
            <TableHead className="w-44 text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.length === 0 ? (
            <TableRow>
              <TableCell colSpan={7} className="text-muted-foreground py-10 text-center">
                No time-off requests.
              </TableCell>
            </TableRow>
          ) : (
            rows.map((r) => (
              <TableRow
                key={r.id}
                className="cursor-pointer"
                onClick={() => actions.onOpen(r)}
              >
                <TableCell className="font-medium">{labels.employeeLabel(r.employee)}</TableCell>
                <TableCell>{LEAVE_TYPE_LABEL[r.type]}</TableCell>
                <TableCell>
                  <StatusBadge status={r.status} />
                </TableCell>
                <TableCell className="text-muted-foreground whitespace-nowrap">
                  {formatDate(r.start_date)}
                </TableCell>
                <TableCell className="text-muted-foreground hidden whitespace-nowrap sm:table-cell">
                  {formatDate(r.end_date)}
                </TableCell>
                <TableCell className="text-muted-foreground hidden md:table-cell">
                  {labels.approverLabel(r.approver)}
                </TableCell>
                <TableCell className="text-right">
                  <RowActionButtons row={r} actions={actions} />
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </div>
  );
}

function TimeOffCards({ rows, labels, actions }: ViewProps) {
  return (
    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
      {rows.map((r) => (
        <Card
          key={r.id}
          role="button"
          tabIndex={0}
          onClick={() => actions.onOpen(r)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              actions.onOpen(r);
            }
          }}
          className="hover:bg-accent/30 cursor-pointer transition-colors"
        >
          <CardHeader>
            <CardTitle className="text-base">{labels.employeeLabel(r.employee)}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-xs">
            <div className="flex flex-wrap items-center gap-2">
              <StatusBadge status={r.status} />
              <span className="text-muted-foreground">{LEAVE_TYPE_LABEL[r.type]}</span>
              <span className="text-muted-foreground ml-auto whitespace-nowrap">
                {formatDate(r.start_date)} → {formatDate(r.end_date)}
              </span>
            </div>
            <div className="flex items-center justify-end">
              <RowActionButtons row={r} actions={actions} />
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

function TimeOffList({ rows, labels, actions }: ViewProps) {
  return (
    <ul className="divide-y rounded-md border">
      {rows.map((r) => (
        <li key={r.id} className="flex items-center gap-3 px-3 py-2">
          {/* Real <button> so the row is interactive a11y-correctly. */}
          <button
            type="button"
            onClick={() => actions.onOpen(r)}
            className="hover:bg-accent/30 -mx-3 flex min-w-0 flex-1 cursor-pointer items-center gap-3 rounded px-3 py-1 text-left"
          >
            <span className="min-w-0 flex-1 truncate text-sm font-semibold">
              {labels.employeeLabel(r.employee)}
            </span>
            <span className="text-muted-foreground hidden truncate text-xs sm:inline">
              {LEAVE_TYPE_LABEL[r.type]}
            </span>
            <StatusBadge status={r.status} />
            <span className="text-muted-foreground hidden whitespace-nowrap text-xs md:inline">
              {formatDate(r.start_date)} → {formatDate(r.end_date)}
            </span>
          </button>
          <span className="flex shrink-0 items-center">
            <RowActionButtons row={r} actions={actions} />
          </span>
        </li>
      ))}
    </ul>
  );
}

function TimeOffGroupedByStatus({ rows, labels, actions }: ViewProps) {
  // Bucket by status, preserving the declared status order; skip empty groups.
  const groups = useMemo(
    () =>
      LEAVE_STATUSES.map((s) => ({
        key: s.value,
        label: s.label,
        bucket: rows.filter((r) => r.status === s.value),
      })),
    [rows],
  );

  return (
    <div className="space-y-6">
      {groups.map(({ key, label, bucket }) => {
        if (bucket.length === 0) return null;
        return (
          <section key={key} aria-labelledby={`time-off-status-${key}`}>
            <h2
              id={`time-off-status-${key}`}
              className="text-muted-foreground mb-2 flex items-center gap-2 text-xs font-semibold tracking-wider uppercase"
            >
              {label}
              <Badge variant="outline" className="text-[10px]">
                {bucket.length}
              </Badge>
            </h2>
            <TimeOffCards rows={bucket} labels={labels} actions={actions} />
          </section>
        );
      })}
    </div>
  );
}

function TimeOffSkeleton() {
  return (
    <div className="space-y-3">
      {Array.from({ length: 4 }).map((_, i) => (
        <Skeleton key={i} className="h-12 w-full" />
      ))}
    </div>
  );
}
